import { useState, useRef, useEffect } from 'react';
import { NavLink, Link } from 'react-router-dom';
import { DetailDrawer } from '@xcentral/ui';
import { useNotifications } from '../features/notifications/useNotifications';
import { NotificationPanel } from '../features/notifications/NotificationPanel';
import { useAuth } from '../auth/useAuth';
import { HelpButton } from '../components/HelpButton';
import PersonalContactsPanel from '../pages/my-workspace/PersonalContactsPanel';
import {
  type AreaId,
  type NavArea,
  SETTINGS_AREA_ID,
  areaById,
  canSeeSettings,
  visibleItems,
  visibleTabAreas,
} from './navRegistry';
import { EnvironmentBadge } from './Sidebar';
import type { ShellViewport } from './AdminLayout';

// Story #22190 (BR-MOB-001) — every top-bar control is at least 44 × 44 px on a phone. From 768px up
// each control keeps exactly the size it had before, so tablet and desktop are unchanged.
const PHONE_TARGET = 'min-h-11 min-w-11 md:min-h-0 md:min-w-0';

interface TopbarProps {
  /**
   * Story #22190 — the shell layout (see AdminLayout). On a `phone` the horizontal area strip is not
   * rendered at all; the drawer carries the areas instead (BR-MOB-001). Defaults to `desktop`.
   */
  viewport?: ShellViewport;
  onMenuClick: () => void;
  /** The currently selected nav area (route-derived; see AdminLayout) — #14378. */
  activeAreaId: AreaId;
  /** Select an area from the top menu. Needed for areas that have no route yet (Procurement). */
  onSelectArea: (id: AreaId) => void;
}

// Story #14378 — the top bar carries the horizontal AREA menu (the slot the operator reserved for
// it on 2026-07-19) plus a right-aligned Settings gear; the sidebar then shows only the selected
// area's submenu. The area map + RBAC live in navRegistry.ts (BR-NAV-006).
//
// A tab navigates to its area's first visible route AND selects the area. Areas whose screens
// aren't built yet (Procurement — F3.18 S6/C6-C7) have no route, so their tab only selects the
// area; the submenu then shows the "Soon" items. Selection is derived from the route on every
// navigation (AdminLayout), so the two never disagree.
function areaTabClasses(isActive: boolean): string {
  return [
    'flex items-center gap-1.5 h-full px-3 whitespace-nowrap text-sm font-medium',
    'border-b-2 transition-colors',
    isActive
      ? 'border-indigo-600 text-indigo-700'
      : 'border-transparent text-gray-600 hover:text-gray-900 hover:bg-gray-50',
  ].join(' ');
}

function AreaTab({
  area,
  isActive,
  href,
  onSelect,
}: {
  area: NavArea;
  isActive: boolean;
  href?: string;
  onSelect: () => void;
}) {
  const content = (
    <>
      {/* Decoration only — the label carries the meaning, so the icon yields first when the
          strip is tight (8 areas + the right-hand toolbar share one 64px row). */}
      <span className="hidden 2xl:inline text-base" aria-hidden="true">{area.icon}</span>
      <span>{area.label}</span>
    </>
  );
  // No route yet (every item is `future`) — the tab still selects the area so its submenu shows.
  if (!href) {
    return (
      <button
        type="button"
        onClick={onSelect}
        aria-current={isActive ? 'page' : undefined}
        className={areaTabClasses(isActive)}
      >
        {content}
      </button>
    );
  }
  return (
    <Link
      to={href}
      onClick={onSelect}
      aria-current={isActive ? 'page' : undefined}
      className={areaTabClasses(isActive)}
    >
      {content}
    </Link>
  );
}

export function Topbar({ viewport = 'desktop', onMenuClick, activeAreaId, onSelectArea }: TopbarProps) {
  const showAreaStrip = viewport !== 'phone';
  const [panelOpen, setPanelOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  // Wave-1 B1 #14203 — Personal contacts moved out of the retired /my-workspace page into a
  // settings flyout (DetailDrawer), reached from the profile menu. Same RBAC as before
  // (PersonalContactsController: Owner,Admin — narrower than My Collaboration's
  // Owner,Operations,Admin, since this is the mailbox owner's own personal address book).
  const [personalContactsOpen, setPersonalContactsOpen] = useState(false);
  const profileRef = useRef<HTMLDivElement>(null);
  const { notifications, unreadCount, markAsRead, markAllRead, dismiss } = useNotifications();
  const { displayName, email, roles, logout } = useAuth();

  const initials = displayName
    ? displayName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
    : email?.slice(0, 2).toUpperCase() ?? '??';

  // My Collaboration (Wave-1 B1 #14203) supersedes the retired /my-workspace page — the
  // top-bar quick-access link now points at the Hub's My Collaboration tab. Same role posture
  // as the page it replaces + the API (EmailReviewController/SuppressionsController —
  // Owner/Operations/Admin — mirrored by HubCollaborationController).
  const canSeeWorkspace = roles.some(r => ['Owner', 'Operations', 'Admin'].includes(r));
  const canManagePersonalContacts = roles.some(r => ['Owner', 'Admin'].includes(r));

  // #14378 — the area menu, RBAC-filtered. An area is hidden when the caller can see none of its
  // items; Owner sees all (navRegistry.hasRequiredRole).
  const tabAreas = visibleTabAreas(roles);
  const firstRouteOf = (area: NavArea): string | undefined =>
    visibleItems(area, roles).find(item => item.path)?.path;
  const settingsHref = canSeeSettings(roles) ? firstRouteOf(areaById(SETTINGS_AREA_ID)) : undefined;

  // Close profile dropdown on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (profileRef.current && !profileRef.current.contains(e.target as Node)) {
        setProfileOpen(false);
      }
    }
    if (profileOpen) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [profileOpen]);

  return (
    <>
      <header className="sticky top-0 z-30 flex h-16 items-center justify-between border-b border-gray-200 bg-white px-3 shadow-sm md:px-4">
        {/* Menu button — opens the sidebar drawer wherever the sidebar is not docked, i.e. below
            1024px (BR-MOB-010). 44 × 44 px target (BR-MOB-001). */}
        <button
          onClick={onMenuClick}
          className="lg:hidden min-h-11 min-w-11 flex-shrink-0 p-2 text-gray-500 hover:text-gray-700"
          aria-label="Open menu"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>

        {/* Environment badge (BR-MOB-001) — the docked sidebar shows it from 1024px up; below that
            the sidebar is a closed drawer, so the bar carries it. flex-shrink-0 so it never
            overlaps or is squeezed by a neighbouring control. */}
        <EnvironmentBadge className="ml-1 lg:hidden" />

        {/* Area menu (#14378) — this is the "horizontal section menu" the operator reserved this
            prominent left slot for on 2026-07-19. Scrolls horizontally rather than wrapping
            (BR-NAV-007). Story #22190 (BR-MOB-001): not rendered on a phone, where it was squeezed
            into ~47px — the drawer's area grid carries the areas there. */}
        {showAreaStrip ? (
          <nav
            aria-label="Areas"
            className="ml-2 hidden h-full min-w-0 flex-1 items-stretch overflow-x-auto md:flex"
          >
            {tabAreas.map(area => (
              <AreaTab
                key={area.id}
                area={area}
                isActive={activeAreaId === area.id}
                href={firstRouteOf(area)}
                onSelect={() => onSelectArea(area.id)}
              />
            ))}
          </nav>
        ) : (
          <div className="min-w-0 flex-1" aria-hidden="true" />
        )}

        {/* Right side */}
        <div className="ml-2 flex items-center gap-1 md:ml-4 md:gap-3">
          {/* My Collaboration quick access (Wave-1 B1 #14203) — compacted to an icon and moved
              into the right cluster now that the area menu occupies the left slot it was holding
              (operator 2026-07-19). Same route + RBAC + active styling as before. */}
          {canSeeWorkspace && (
            <NavLink
              to="/hub/collaboration"
              className={({ isActive }) =>
                `inline-flex items-center justify-center rounded-md w-9 h-9 ${PHONE_TARGET} transition-colors ${
                  isActive ? 'bg-indigo-600 text-white' : 'text-gray-600 hover:bg-gray-100'
                }`
              }
              title="My Collaboration"
              aria-label="My Collaboration"
            >
              <span className="text-base" aria-hidden="true">📥</span>
            </NavLink>
          )}

          {/* Search (swapped from the prominent left slot — operator 2026-07-19). Held back to
              `lg` so the area menu has room at tablet widths (#14378). */}
          <div className="relative hidden xl:block w-44 2xl:w-60">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              placeholder="Search accounts, contacts..."
              className="w-full h-9 rounded-md border border-gray-300 pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500"
            />
          </div>
          {/* Settings (#14378) — the ninth area, reached from this right-aligned gear rather than
              a top tab. Hidden entirely if the caller can see none of its items. */}
          {settingsHref && (
            <Link
              to={settingsHref}
              onClick={() => onSelectArea(SETTINGS_AREA_ID)}
              aria-label="Settings"
              title="Settings"
              aria-current={activeAreaId === SETTINGS_AREA_ID ? 'page' : undefined}
              className={`inline-flex items-center justify-center rounded-md w-9 h-9 ${PHONE_TARGET} transition-colors ${
                activeAreaId === SETTINGS_AREA_ID
                  ? 'bg-indigo-600 text-white'
                  : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'
              }`}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </Link>
          )}
          {/* HelpButton is shared; size its button from here so the 44px phone target holds
              without touching the component. `contents` keeps the wrapper out of the layout. */}
          <span className="contents [&>button]:min-h-11 [&>button]:min-w-11 md:[&>button]:min-h-0 md:[&>button]:min-w-0">
            <HelpButton />
          </span>
          {/* Notification bell */}
          <button
            onClick={() => setPanelOpen(!panelOpen)}
            className={`relative p-2 ${PHONE_TARGET} text-gray-500 hover:text-gray-700`}
            aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ''}`}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
            </svg>
            {unreadCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </button>

          {/* User profile */}
          <div className="relative" ref={profileRef}>
            <button
              onClick={() => setProfileOpen(!profileOpen)}
              className={`flex items-center justify-center gap-2 rounded-md p-1 ${PHONE_TARGET} hover:bg-gray-100`}
              aria-label="User menu"
            >
              <div className="w-8 h-8 rounded-full bg-indigo-600 flex items-center justify-center text-white text-xs font-medium">
                {initials}
              </div>
              <span className="hidden lg:block text-sm font-medium text-gray-700">{displayName}</span>
              <svg className="hidden lg:block w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {profileOpen && (
              <div className="absolute right-0 mt-2 w-64 rounded-lg border border-gray-200 bg-white shadow-lg z-50">
                <div className="px-4 py-3 border-b border-gray-100">
                  <p className="text-sm font-medium text-gray-900">{displayName}</p>
                  <p className="text-xs text-gray-500 truncate">{email}</p>
                  {roles.length > 0 && (
                    <div className="mt-1 flex flex-wrap gap-1">
                      {roles.map(r => (
                        <span key={r} className="inline-flex items-center rounded bg-indigo-50 px-1.5 py-0.5 text-[10px] font-medium text-indigo-700">{r}</span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="py-1">
                  {/* Wave-1 B1 #14203 — Personal contacts, moved here from the retired
                      /my-workspace page (settings flyout, per the Story's plan). */}
                  {canManagePersonalContacts && (
                    <button
                      onClick={() => { setProfileOpen(false); setPersonalContactsOpen(true); }}
                      className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    >
                      <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                      Personal contacts
                    </button>
                  )}
                  <button
                    onClick={() => { setProfileOpen(false); logout(); }}
                    className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                  >
                    <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                    </svg>
                    Sign out
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Personal contacts settings flyout (Wave-1 B1 #14203) */}
      <DetailDrawer
        open={personalContactsOpen}
        onClose={() => setPersonalContactsOpen(false)}
        title="Personal contacts"
      >
        <PersonalContactsPanel />
      </DetailDrawer>

      {/* Notification panel */}
      <NotificationPanel
        open={panelOpen}
        onClose={() => setPanelOpen(false)}
        notifications={notifications}
        onMarkRead={markAsRead}
        onMarkAllRead={markAllRead}
        onDismiss={dismiss}
      />
    </>
  );
}
